import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plane, Ship, Truck, Box, ArrowRight, Ruler, Weight, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EquipmentPage = () => {
  const { t } = useTranslation();
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMode, setActiveMode] = useState('all');

  useEffect(() => {
    fetchEquipment();
  }, []);

  const fetchEquipment = async () => {
    try {
      const response = await axios.get(`${API}/equipment`);
      setEquipment(response.data);
    } catch (error) {
      console.error('Failed to fetch equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'air':
        return Plane;
      case 'ocean':
        return Ship;
      case 'road':
        return Truck;
      default:
        return Box;
    }
  };

  const getModeColor = (mode) => {
    switch (mode) {
      case 'air':
        return 'bg-blue-100 text-blue-800';
      case 'ocean':
        return 'bg-cyan-100 text-cyan-800';
      case 'road':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-steel-100 text-steel-800';
    }
  };

  const filteredEquipment = activeMode === 'all' 
    ? equipment 
    : equipment.filter(e => e.mode === activeMode);

  return (
    <div className="min-h-screen" data-testid="equipment-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy-800">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-10" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-gold text-sm font-bold tracking-widest uppercase mb-4 block">{t('equipment.title')}</span>
            <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('equipment.heading')}
            </h1>
            <p className="text-steel-300 text-xl leading-relaxed">
              {t('equipment.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Equipment Section */}
      <section className="section-padding bg-steel-50">
        <div className="container-custom">
          {/* Filter Tabs */}
          <Tabs value={activeMode} onValueChange={setActiveMode} className="mb-12">
            <TabsList className="bg-white p-1 h-auto flex-wrap justify-start gap-2">
              <TabsTrigger 
                value="all" 
                className="data-[state=active]:bg-navy-800 data-[state=active]:text-white px-6 py-3"
                data-testid="filter-all"
              >
                {t('equipment.filters.all')}
              </TabsTrigger>
              <TabsTrigger 
                value="air" 
                className="data-[state=active]:bg-navy-800 data-[state=active]:text-white px-6 py-3 flex items-center gap-2"
                data-testid="filter-air"
              >
                <Plane className="w-4 h-4" /> {t('equipment.filters.air')}
              </TabsTrigger>
              <TabsTrigger 
                value="ocean" 
                className="data-[state=active]:bg-navy-800 data-[state=active]:text-white px-6 py-3 flex items-center gap-2"
                data-testid="filter-ocean"
              >
                <Ship className="w-4 h-4" /> {t('equipment.filters.ocean')}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Equipment Grid */}
          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-steel-600">{t('equipment.loading')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEquipment.map((item, index) => {
                const ModeIcon = getModeIcon(item.mode);
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1" data-testid={`equipment-card-${item.id}`}>
                      <CardContent className="p-6">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-navy-800 flex items-center justify-center">
                              <ModeIcon className="w-6 h-6 text-gold" />
                            </div>
                            <div>
                              <h3 className="font-outfit font-bold text-lg text-navy-800">{item.name}</h3>
                              <p className="text-sm text-steel-500">{t('equipmentMgmt.code')}: {item.code}</p>
                            </div>
                          </div>
                          <Badge className={getModeColor(item.mode)}>
                            {item.mode}
                          </Badge>
                        </div>

                        {/* Category */}
                        <p className="text-sm text-steel-600 mb-4">{item.category}</p>

                        {/* Dimensions */}
                        <div className="bg-steel-50 p-4 rounded-lg mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Ruler className="w-4 h-4 text-gold" />
                            <span className="text-sm font-medium text-navy-800">{t('equipment.dimensions')}</span>
                          </div>
                          <p className="text-sm text-steel-600">
                            {item.dimensions.length_cm} × {item.dimensions.width_cm} × {item.dimensions.height_cm} cm
                          </p>
                        </div>

                        {/* Capacity */}
                        <div className="bg-steel-50 p-4 rounded-lg mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Weight className="w-4 h-4 text-gold" />
                            <span className="text-sm font-medium text-navy-800">{t('equipment.capacity')}</span>
                          </div>
                          <p className="text-sm text-steel-600">
                            {t('equipment.maxWeight')}: {item.capacity.max_weight_kg?.toLocaleString()} kg
                            {item.capacity.max_volume_cbm > 0 && (
                              <span className="block">{t('equipment.maxVolume')}: {item.capacity.max_volume_cbm} m³</span>
                            )}
                          </p>
                        </div>

                        {/* Recommended Use */}
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Info className="w-4 h-4 text-gold" />
                            <span className="text-sm font-medium text-navy-800">{t('equipment.recommendedFor')}</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {item.recommended_use.slice(0, 3).map((use) => (
                              <span key={use} className="text-xs bg-gold/10 text-gold-dark px-2 py-1 rounded">
                                {use}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Restrictions */}
                        {item.restrictions.length > 0 && (
                          <p className="text-xs text-steel-500 italic">
                            {item.restrictions[0]}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-navy-800">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-6">
              {t('equipment.cta.title')}
            </h2>
            <p className="text-steel-300 text-lg mb-8">
              {t('equipment.cta.description')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/quote" data-testid="equipment-quote-btn">
                <Button className="btn-accent">
                  {t('hero.requestQuote')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white px-8 py-4 uppercase tracking-widest text-sm font-bold">
                  {t('equipment.cta.contactExpert')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EquipmentPage;
